/**
 * Verbes métier du matériel.
 *
 * Lire est N1 et ouvert à tous : la base décide de ce que chacun voit. Tout le
 * reste est N2 — rien ne sort de l'entreprise, et chaque geste se défait : une
 * machine déplacée se ramène, une machine sortie du parc y revient.
 *
 * Les rôles sont dits ici **et** en base : ici pour refuser proprement avant
 * l'appel, en base pour que le refus tienne quel que soit le chemin. Le chef de
 * chantier n'est borné à ses chantiers qu'en base : le noyau ne connaît pas
 * ses affectations.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext, Role } from "../../context.js";
import type {
  AddBulkItemInput,
  AddEquipmentInput,
  ArchiveBulkItemInput,
  ArchiveEquipmentInput,
  EquipmentHistoryInput,
  ListEquipmentInput,
  MoveBulkItemInput,
  MoveEquipmentInput,
  SetEquipmentPhotoInput,
  UpdateBulkItemInput,
  UpdateEquipmentInput,
} from "./schema.js";
import type { EquipmentFleet, EquipmentMovement } from "./types.js";

export type EquipmentDetails = Omit<AddEquipmentInput, "confirmationId" | "projectId">;
export type BulkItemDetails = Omit<AddBulkItemInput, "confirmationId">;
/** Un lieu : le dépôt, la réparation, ou un chantier désigné. */
export interface EquipmentPlace {
  kind: MoveEquipmentInput["toKind"];
  projectId: string | null;
}

export interface EquipmentRepository {
  /** Le parc que l'appelant voit — la base filtre. */
  list(ctx: ExecutionContext, includeArchived: boolean): Promise<EquipmentFleet>;
  history(ctx: ExecutionContext, filter: { equipmentId?: string; bulkItemId?: string }): Promise<EquipmentMovement[]>;

  add(ctx: ExecutionContext, details: EquipmentDetails, projectId: string | null): Promise<{ id: string }>;
  update(ctx: ExecutionContext, id: string, details: EquipmentDetails): Promise<void>;
  setArchived(ctx: ExecutionContext, id: string, reason: ArchiveEquipmentInput["reason"] | null): Promise<void>;
  setPhoto(ctx: ExecutionContext, id: string, photoPath: string | null): Promise<void>;
  move(
    ctx: ExecutionContext,
    id: string,
    to: { kind: MoveEquipmentInput["toKind"]; projectId: string | null },
    note: string | null,
  ): Promise<void>;

  addBulk(ctx: ExecutionContext, details: BulkItemDetails): Promise<{ id: string }>;
  updateBulk(ctx: ExecutionContext, id: string, details: BulkItemDetails): Promise<void>;
  setBulkArchived(ctx: ExecutionContext, id: string, reason: ArchiveBulkItemInput["reason"] | null): Promise<void>;
  moveBulk(
    ctx: ExecutionContext,
    id: string,
    move: { from: EquipmentPlace; to: EquipmentPlace; quantity: number },
    note: string | null,
  ): Promise<void>;
}

const FLEET_ROLES: readonly Role[] = ["patron"];
const MOVER_ROLES: readonly Role[] = ["patron", "conducteur", "assistant", "chef_de_chantier"];
const HISTORY_ROLES: readonly Role[] = ["patron", "conducteur", "assistant", "lecture_seule"];

function details<T extends EquipmentDetails>(input: T): EquipmentDetails {
  const rented = input.ownership === "rented";
  // Une machine à soi n'a pas de loueur : on efface plutôt que de refuser, le
  // formulaire a pu garder la saisie d'avant le changement d'avis.
  return {
    name: input.name.trim(),
    typeLabel: input.typeLabel.trim(),
    category: input.category,
    brand: input.brand ?? null,
    model: input.model ?? null,
    serialNumber: input.serialNumber ?? null,
    ownership: input.ownership,
    rentalCompany: rented ? (input.rentalCompany ?? null) : null,
    rentalReturnOn: rented ? (input.rentalReturnOn ?? null) : null,
    notes: input.notes ?? null,
  };
}

function bulkDetails<T extends BulkItemDetails>(input: T): BulkItemDetails {
  if (!Number.isInteger(input.totalQuantity) || input.totalQuantity < 0) {
    throw new DomainError("invalid_input", "La quantité est un nombre entier, zéro ou plus.");
  }
  const rented = input.ownership === "rented";
  return {
    name: input.name.trim(),
    typeLabel: input.typeLabel.trim(),
    category: input.category,
    unit: input.unit.trim() || "unité",
    ownership: input.ownership,
    rentalCompany: rented ? (input.rentalCompany ?? null) : null,
    rentalReturnOn: rented ? (input.rentalReturnOn ?? null) : null,
    totalQuantity: input.totalQuantity,
    notes: input.notes ?? null,
  };
}

function place(kind: EquipmentPlace["kind"], projectId: string | null | undefined): EquipmentPlace {
  const id = projectId ?? null;
  if ((kind === "chantier") !== (id !== null)) {
    throw new DomainError("invalid_input", "Un chantier se désigne ; le dépôt et la réparation non.");
  }
  return { kind, projectId: id };
}

export function createEquipmentServices(repo: EquipmentRepository) {
  const listerMateriel = defineService<ListEquipmentInput, EquipmentFleet>({
    name: "lister_materiel",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, input.includeArchived ?? false),
  });

  const historiqueMateriel = defineService<EquipmentHistoryInput, EquipmentMovement[]>({
    name: "historique_materiel",
    level: ActionLevel.Read,
    allowedRoles: HISTORY_ROLES,
    handler: (ctx, input) => {
      if (!input.equipmentId === !input.bulkItemId) {
        throw new DomainError("invalid_input", "Désignez une machine ou un outillage.");
      }
      return repo.history(ctx, {
        ...(input.equipmentId ? { equipmentId: input.equipmentId } : {}),
        ...(input.bulkItemId ? { bulkItemId: input.bulkItemId } : {}),
      });
    },
  });

  const ajouterMateriel = defineService<AddEquipmentInput, { id: string }>({
    name: "ajouter_materiel",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: (ctx, input) => repo.add(ctx, details(input), input.projectId ?? null),
  });

  const modifierMateriel = defineService<UpdateEquipmentInput, { id: string }>({
    name: "modifier_materiel",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.update(ctx, input.id, details(input));
      return { id: input.id };
    },
  });

  const sortirMateriel = defineService<ArchiveEquipmentInput, { archived: true }>({
    name: "sortir_materiel_du_parc",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.setArchived(ctx, input.id, input.reason);
      return { archived: true };
    },
  });

  const remettreMateriel = defineService<{ confirmationId?: string; id: string }, { archived: false }>({
    name: "remettre_materiel_au_parc",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.setArchived(ctx, input.id, null);
      return { archived: false };
    },
  });

  const changerPhotoMateriel = defineService<SetEquipmentPhotoInput, { id: string }>({
    name: "changer_photo_materiel",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      // La base le vérifie aussi ; dit ici, le refus arrive avant l'appel.
      if (input.photoPath !== null && !input.photoPath.startsWith(`${ctx.tenantId}/${input.id}/`)) {
        throw new DomainError("invalid_input", "Cette photo n'est pas rangée avec cette machine.");
      }
      await repo.setPhoto(ctx, input.id, input.photoPath);
      return { id: input.id };
    },
  });

  const deplacerMateriel = defineService<MoveEquipmentInput, { moved: true }>({
    name: "deplacer_materiel",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: MOVER_ROLES,
    handler: async (ctx, input) => {
      const projectId = input.toProjectId ?? null;
      if ((input.toKind === "chantier") !== (projectId !== null)) {
        throw new DomainError("invalid_input", "Un chantier se désigne ; le dépôt et la réparation non.");
      }
      await repo.move(ctx, input.id, { kind: input.toKind, projectId }, input.note ?? null);
      return { moved: true };
    },
  });

  const ajouterOutillage = defineService<AddBulkItemInput, { id: string }>({
    name: "ajouter_outillage",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: (ctx, input) => repo.addBulk(ctx, bulkDetails(input)),
  });

  const modifierOutillage = defineService<UpdateBulkItemInput, { id: string }>({
    name: "modifier_outillage",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.updateBulk(ctx, input.id, bulkDetails(input));
      return { id: input.id };
    },
  });

  const sortirOutillage = defineService<ArchiveBulkItemInput, { archived: true }>({
    name: "sortir_outillage_du_parc",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.setBulkArchived(ctx, input.id, input.reason);
      return { archived: true };
    },
  });

  const remettreOutillage = defineService<{ confirmationId?: string; id: string }, { archived: false }>({
    name: "remettre_outillage_au_parc",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: FLEET_ROLES,
    handler: async (ctx, input) => {
      await repo.setBulkArchived(ctx, input.id, null);
      return { archived: false };
    },
  });

  const deplacerOutillage = defineService<MoveBulkItemInput, { moved: true }>({
    name: "deplacer_outillage",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: MOVER_ROLES,
    handler: async (ctx, input) => {
      if (!Number.isInteger(input.quantity) || input.quantity < 1) {
        throw new DomainError("invalid_input", "Dites combien.");
      }
      const from = place(input.fromKind, input.fromProjectId);
      const to = place(input.toKind, input.toProjectId);
      if (from.kind === to.kind && from.projectId === to.projectId) {
        throw new DomainError("invalid_input", "L'origine et la destination sont les mêmes.");
      }
      await repo.moveBulk(ctx, input.id, { from, to, quantity: input.quantity }, input.note ?? null);
      return { moved: true };
    },
  });

  return {
    listerMateriel,
    historiqueMateriel,
    ajouterMateriel,
    modifierMateriel,
    sortirMateriel,
    remettreMateriel,
    changerPhotoMateriel,
    deplacerMateriel,
    ajouterOutillage,
    modifierOutillage,
    sortirOutillage,
    remettreOutillage,
    deplacerOutillage,
  };
}

export type EquipmentServices = ReturnType<typeof createEquipmentServices>;
